import { useSelector } from '@xstate/react';
import { createEmptyActor, type SnapshotFrom } from 'xstate';
import { useMalpracticeRef } from '../../account/vaultSelectors';
import { hasActiveInsuranceParse } from '../../documents/utils';
import type { malpracticeMachine } from '../malpractice.machine';
import { useMalpracticeActorRef } from '../utils';
import type { certificateUploadMachine } from './certificateUpload.machine';

const emptyActor = createEmptyActor();

const selectCertificateUploadRef = (state: SnapshotFrom<typeof malpracticeMachine>) =>
  state.context.certificateUploadRef;
const selectIsUploading = (state: SnapshotFrom<typeof certificateUploadMachine>) =>
  state.matches('$_UPLOADING');
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- emptyActor snapshots have no context
const selectHasActiveParse = (state: any) =>
  hasActiveInsuranceParse(state?.context?.documentReferences ?? []);

/**
 * What the Malpractice Insurance tab needs to upload a certificate and show that one is being
 * read: the upload goes through the documents service, and the server parses it on arrival.
 */
export const useInsuranceCertificateUpload = () => {
  const malpracticeRef = useMalpracticeActorRef();
  const { provider, documentsService } = useMalpracticeRef();
  const certificateUploadRef = useSelector(malpracticeRef, selectCertificateUploadRef);
  const uploadingCertificate = useSelector(certificateUploadRef, selectIsUploading);
  const parsingCertificate = useSelector(documentsService ?? emptyActor, selectHasActiveParse);
  const providerId = provider?.id;

  const onUploadCertificate = (file: File) =>
    certificateUploadRef.send({ type: 'ON_UPLOAD_CERTIFICATE', payload: { file, providerId } });

  return { onUploadCertificate, uploadingCertificate, parsingCertificate };
};
